/* A presentational component plus the state shape it edits, so fast refresh's
   component-only rule does not apply here. */
/* eslint-disable react-refresh/only-export-components */
import type {RichText, TextFormat} from '../lib/rich-text';
import {resolveVariables} from '../lib/template-variables';
import {RichTextEditor} from './RichTextEditor';
import {RichTextView} from './RichTextView';

/**
 * A label the author can rename in place, on the canvas.
 *
 * Double-clicking a heading turns it into an editor sized to its content;
 * everything else about the element stays where it is, so renaming does not
 * reflow the page under the cursor. The editor holds styles on part of the text
 * (DERP-307): select some words and Bold, or the toolbar, formats only those.
 */

export interface InlineEditState {
  id: string | null;
  value: RichText;
  onChange: (v: RichText) => void;
  onCommit: () => void;
  onCancel: () => void;
}

// Variables resolve against the fill context (keyed by variable key). The editor
// passes an empty context, so tokens render literally as {{…}}.
export const substituteVars = resolveVariables;

export function InlineLabel({
  value,
  sampleData,
  base,
  itemId,
  className,
  style,
  prefix,
  inlineEdit,
}: {
  /** The label as authored, with the styles on part of it. */
  value: RichText;
  sampleData: Record<string, string>;
  /** What the label inherits from its element. */
  base: TextFormat;
  itemId: string;
  className?: string;
  style?: React.CSSProperties;
  prefix?: string;
  inlineEdit?: InlineEditState;
}) {
  if (inlineEdit?.id === itemId) {
    return (
      <RichTextEditor
        value={inlineEdit.value}
        onChange={inlineEdit.onChange}
        owner={itemId}
        base={base}
        onBlurAway={inlineEdit.onCommit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            inlineEdit.onCommit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            inlineEdit.onCancel();
          }
        }}
        className={`${className ?? ''} border-b border-indigo-400 bg-transparent`}
        style={{display: 'block', width: '100%', minWidth: 40, ...style}}
      />
    );
  }
  return (
    <span className={className} style={{whiteSpace: 'pre-wrap', ...style}}>
      <RichTextView value={value} sampleData={sampleData} prefix={prefix} />
    </span>
  );
}
